import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useThemeMode } from '@/shared/ui/basic';
import { performLogout, getAuthSession, apiFetch, isDemoUser } from '@/shared/api/client';
import { STALE_TIME_MEDIUM } from '@/shared/config';
import { useProfile } from '@/entities/user';
import { usePageTitle as useDynamicPageTitle } from '@/shared/hooks/usePageTitle';
import { useCommandPaletteBoardContext } from '@/shared/ui/commandPaletteContext';

// Heavy modal widgets — lazily loaded the first time they're opened.
const CommandPalette = lazy(() =>
  import('@/shared/ui/CommandPalette').then((m) => ({ default: m.CommandPalette })),
);
const KeyboardHelp = lazy(() =>
  import('@/shared/ui/KeyboardHelp').then((m) => ({ default: m.KeyboardHelp })),
);
import { LibreCardsIcon, APP_NAME } from '@/shared/ui/LibreCardsLogo';
import { NotificationsDropdown } from '@/shared/ui/NotificationsDropdown';
import { RealtimeClient } from '@/shared/realtime';
import { Avatar } from '@/shared/ui/Avatar';
import {
  LayoutDashboard,
  Star,
  FolderKanban,
  Users,
  ShieldCheck,
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Plus,
} from 'lucide-react';

type BoardNav = {
  id: number;
  name: string;
  shortCode: string;
  scope?: 'personal' | 'team' | 'project-personal' | 'project-team';
  workgroupName?: string | null;
  projectName?: string | null;
};

/* ── layout ── */
const SIDEBAR_WIDE = 240;
const SIDEBAR_NARROW = 60;

const Shell = styled.div<{ $collapsed?: boolean }>`
  height: 100vh;
  display: grid;
  grid-template-columns: ${({ $collapsed }) => ($collapsed ? SIDEBAR_NARROW : SIDEBAR_WIDE)}px minmax(0, 1fr);
  transition: grid-template-columns 0.2s ease;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Sidebar = styled.aside<{ $collapsed?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? SIDEBAR_NARROW : SIDEBAR_WIDE)}px;
  border-right: 1px solid var(--border-subtle);
  background: var(--bg-sidebar);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;
  transition: width 0.2s ease;
  @media (max-width: 768px) { display: none; }

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: transparent; border-radius: 2px; }
  &:hover::-webkit-scrollbar-thumb { background: var(--border-subtle); }
`;

const WsHeader = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px ${({ $collapsed }) => ($collapsed ? '0' : '16px')} 8px;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  min-height: 52px;
`;

const CollapseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid var(--border-subtle);
  background: var(--bg-hover);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  flex-shrink: 0;
  transition: all 0.12s ease;
  svg { width: 16px; height: 16px; }
  &:hover { background: var(--accent-muted); border-color: var(--accent-border); color: var(--accent); }
`;

const WsLogo = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--accent), #6366f1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const WsName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
`;


const SectionHeader = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary);
  transition: color 0.1s ease;
  &:hover { color: var(--text-secondary); }
  svg { width: 12px; height: 12px; transition: transform 0.15s ease;
    transform: ${({ $open }) => ($open === false ? 'rotate(-90deg)' : 'rotate(0)')};
  }
`;

const NavGroup = styled.nav<{ $collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0 8px;
  max-height: ${({ $collapsed }) => ($collapsed ? '0' : '600px')};
  overflow: hidden;
  transition: max-height 0.2s ease;
`;

const NavRow = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  text-decoration: none;
  transition: background-color 0.08s ease, color 0.08s ease;
  svg { opacity: 0.55; flex-shrink: 0; transition: opacity 0.1s ease; }
  &:hover { background: var(--bg-hover); color: var(--text-primary); svg { opacity: 0.85; } }
  &.active { background: var(--bg-hover); color: var(--text-primary); font-weight: 600; svg { opacity: 1; } }
`;

const NavCount = styled.span`
  margin-left: auto;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-tertiary);
  background: var(--bg-hover);
  padding: 1px 5px;
  border-radius: 4px;
`;

const BoardDot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: 3px;
  background: ${({ $c }) => $c};
  flex-shrink: 0;
  margin-right: 4px;
`;

const Spacer = styled.div`flex: 1;`;

const UserSection = styled.div`
  padding: 10px 12px 14px;
  border-top: 1px solid var(--border-subtle);
`;

const UserRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background 0.12s ease;
  cursor: pointer;

  &:hover {
    background: var(--bg-hover);
  }
`;

const UserInfo = styled.div`flex: 1; min-width: 0;`;
const UserName = styled.div`font-size: 14px; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const UserEmail = styled.div`font-size: 12px; color: var(--text-tertiary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;

const ActionRow = styled.div`display: flex; gap: 4px; margin-top: 8px;`;

const SmallBtn = styled.button`
  flex: 1;
  padding: 5px 0;
  border-radius: 6px;
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: inherit;
  transition: all 0.1s ease;
  svg { width: 13px; height: 13px; }
  &:hover { background: var(--bg-hover); color: var(--text-primary); }
`;

const Badge = styled.span`
  position: absolute;
  top: 1px;
  right: 1px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #e5484d;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

/* ── demo banner ── */
const DemoBanner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 16px;
  background: linear-gradient(135deg, #7c8cff, #6366f1);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-align: center;
  user-select: none;

  svg { flex-shrink: 0; opacity: 0.85; }

  a {
    color: #fff;
    text-decoration: underline;
    text-underline-offset: 2px;
    opacity: 0.9;
    transition: opacity 0.1s;
    &:hover { opacity: 1; }
  }
`;

/* ── content ── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}`;

const Content = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 24px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--bg-sidebar);
  position: sticky;
  top: 0;
  z-index: 10;
  min-height: 44px;
`;

const MobileNav = styled.div`
  display: none;
  padding: 10px 16px 0;
  gap: 8px;
  overflow-x: auto;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileNavLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  text-decoration: none;
  color: var(--text-secondary);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  background: var(--bg-primary);

  &.active {
    color: var(--accent);
    border-color: var(--accent);
    background: var(--accent-muted);
  }
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
`;

const BcSep = styled.span`color: var(--text-tertiary); opacity: 0.35; font-size: 10px;`;

const BreadcrumbLink = styled(Link)`
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.1s ease;

  &:hover {
    color: var(--text-primary);
  }
`;

const BreadcrumbCurrent = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

/** Подписи сегментов пути для «хлебных крошек» (URL остаётся латиницей) */
const PATH_SEGMENT_LABELS: Record<string, string> = {
  boards: 'Доски',
  projects: 'Проекты',
  workgroups: 'Команды',
  'my-work': 'Моя работа',
  settings: 'Настройки',
  profile: 'Профиль',
  security: 'Безопасность',
  admin: 'Администрирование',
  login: 'Вход',
  signup: 'Регистрация',
  forgot: 'Восстановление',
  reset: 'Сброс пароля',
};

const breadcrumbSegmentLabel = (segment: string, index: number, total: number, pageTitle: string) => {
  if (index === total - 1) return pageTitle;
  return PATH_SEGMENT_LABELS[segment] ?? segment;
};

const MainArea = styled.main`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* Inner scroll region: the app shell (sidebar + top bar) stays fixed to the viewport,
     and page content scrolls here instead of the window. Pages that fill the height
     (e.g. the kanban board) scroll internally and never grow this container. */
  overflow: auto;
  padding: 20px 24px 32px;
  animation: ${fadeIn} 0.18s ease;
`;

type Props = { title: string; children: React.ReactNode };

const boardColors = ['#7c8cff', '#34d399', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#f97316'];

/* ── Winter garland (controlled by localStorage "winter") ── */
const garlandGlow = keyframes`
  0%, 100% { opacity: 0.55; filter: blur(0.5px); }
  50% { opacity: 1; filter: blur(1.5px); }
`;
const garlandSwing = keyframes`
  0%, 100% { transform: rotate(-1deg); }
  50% { transform: rotate(1deg); }
`;
const GARLAND_COLORS = ['#ff4444', '#44cc44', '#ffcc00', '#4488ff', '#ff66cc', '#44dddd', '#ff8833'];

const GarlandWrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  z-index: 9999;
  pointer-events: none;
  overflow: hidden;
`;
const GarlandWire = styled.div`
  position: absolute;
  top: 0;
  left: -10px;
  right: -10px;
  height: 32px;
  animation: ${garlandSwing} 4s ease-in-out infinite;
  transform-origin: top center;
`;
const GarlandBulb = styled.span<{ $color: string; $delay: number }>`
  position: absolute;
  width: 8px;
  height: 12px;
  border-radius: 0 0 50% 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 6px 2px ${({ $color }) => $color}88, 0 0 14px 4px ${({ $color }) => $color}44;
  animation: ${garlandGlow} ${({ $delay }) => 1.4 + $delay * 0.3}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay * 0.15}s;
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 2px;
    width: 4px;
    height: 4px;
    border-radius: 1px;
    background: #555;
  }
`;
const GarlandCable = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 32px;
`;

const WinterGarland: React.FC = () => {
  const bulbCount = Math.floor(window.innerWidth / 36);
  const bulbs = useMemo(() => {
    const items: { x: number; y: number; color: string; delay: number }[] = [];
    for (let i = 0; i < bulbCount; i++) {
      const t = i / (bulbCount - 1);
      const x = t * 100;
      const sag = Math.sin(t * Math.PI) * 16;
      items.push({
        x,
        y: 4 + sag,
        color: GARLAND_COLORS[i % GARLAND_COLORS.length],
        delay: i % GARLAND_COLORS.length,
      });
    }
    return items;
  }, [bulbCount]);

  const pathD = useMemo(() => {
    if (bulbs.length < 2) return '';
    const pts = bulbs.map((b) => `${b.x}% ${b.y}`);
    return `M ${pts[0]} ` + pts.slice(1).map((p) => `L ${p}`).join(' ');
  }, [bulbs]);

  return (
    <GarlandWrap>
      <GarlandWire>
        <GarlandCable viewBox={`0 0 ${window.innerWidth} 32`} preserveAspectRatio="none">
          <path d={pathD.replace(/%/g, '')} fill="none" stroke="#333" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </GarlandCable>
        {bulbs.map((b, i) => (
          <GarlandBulb key={i} $color={b.color} $delay={b.delay} style={{ left: `${b.x}%`, top: b.y }} />
        ))}
      </GarlandWire>
    </GarlandWrap>
  );
};

export const LayoutRoot: React.FC<Props> = ({ title, children }) => {
  const { mode, toggle } = useThemeMode();
  const session = getAuthSession();
  const location = useLocation();
  const dynamicTitle = useDynamicPageTitle();
  const paletteBoardContext = useCommandPaletteBoardContext();
  const queryClient = useQueryClient();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [boardsOpen, setBoardsOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true');
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  }, []);
  const [winterMode] = useState(() => localStorage.getItem('winter') === 'true');
  const demoMode = isDemoUser();

  const { data: boards } = useQuery<BoardNav[]>({
    queryKey: ['sidebar-boards'],
    queryFn: () => apiFetch<BoardNav[]>('/api/boards'),
    // Sidebar list barely changes during a session; mutation paths (board
    // create/delete/settings) invalidate this key explicitly, so a longer
    // stale window just prevents idle remount refetches.
    staleTime: STALE_TIME_MEDIUM,
  });
  const { data: profile } = useProfile();
  const { data: unreadPayload } = useQuery<{ count: number }>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiFetch<{ count: number }>('/api/notifications/unread-count'),
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!profile?.id) return;
    const realtime = new RealtimeClient();
    const listenerId = realtime.subscribe({
      scope: 'user',
      id: String(profile.id),
      onEvent: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
          queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
        ]);
      },
      onReconnect: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
          queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
        ]);
      },
    });
    return () => {
      realtime.unsubscribe(listenerId);
      realtime.destroy();
    };
  }, [profile?.id, queryClient]);

  const handleKey = useCallback((e: KeyboardEvent) => {
    const el = e.target as HTMLElement;
    const isInput = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;

    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyK') { e.preventDefault(); setPaletteOpen((p) => !p); return; }
    if (isInput) return;
    if ((e.key === '?' || (e.shiftKey && e.code === 'Slash')) && !e.ctrlKey && !e.metaKey) { e.preventDefault(); setHelpOpen((p) => !p); }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const crumbs = location.pathname.split('/').filter(Boolean);
  const effectiveTitle = dynamicTitle || title;
  const breadcrumbItems = useMemo(() => {
    let currentPath = '';
    return crumbs.map((segment, index) => {
      currentPath += `/${segment}`;
      return {
        href: currentPath,
        label: breadcrumbSegmentLabel(segment, index, crumbs.length, effectiveTitle),
        current: index === crumbs.length - 1,
      };
    });
  }, [crumbs, effectiveTitle]);
  const initial = (profile?.name ?? session?.userName)?.slice(0, 1).toUpperCase() ?? 'К';
  const unreadCount = unreadPayload?.count ?? 0;
  const personalBoards = useMemo(
    () => (boards ?? []).filter((board) => board.scope === 'personal' || board.scope === 'project-personal'),
    [boards],
  );
  const teamBoards = useMemo(
    () => (boards ?? []).filter((board) => board.scope === 'team' || board.scope === 'project-team'),
    [boards],
  );

  return (
    <Shell $collapsed={sidebarCollapsed} style={demoMode ? { paddingTop: 32 } : undefined}>
      {demoMode && (
        <DemoBanner>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="8" cy="8" r="6"/>
            <path d="M8 5v3.5M8 10.5v.5"/>
          </svg>
          Демо-режим — доступен только просмотр. Все изменения заблокированы.
        </DemoBanner>
      )}
      {winterMode && <WinterGarland />}
      <Sidebar $collapsed={sidebarCollapsed} style={demoMode ? { top: 32, height: 'calc(100vh - 32px)' } : undefined}>
        <WsHeader $collapsed={sidebarCollapsed}>
          {!sidebarCollapsed ? (
            <>
              <WsLogo><LibreCardsIcon size={16} color="#fff" /></WsLogo>
              <WsName>{APP_NAME}</WsName>
              <CollapseBtn onClick={toggleSidebar} title="Свернуть меню">
                <ChevronsLeft size={14} />
              </CollapseBtn>
            </>
          ) : (
            <CollapseBtn onClick={toggleSidebar} title="Развернуть меню" style={{ marginLeft: 0 }}>
              <ChevronsRight size={14} />
            </CollapseBtn>
          )}
        </WsHeader>

        <NavGroup style={{ paddingTop: 10 }}>
          <NavRow end to="/boards" title="Доски"><LayoutDashboard size={16} />{!sidebarCollapsed && ' Доски'}</NavRow>
          <NavRow end to="/my-work" title="Моя работа"><Star size={16} />{!sidebarCollapsed && ' Моя работа'}</NavRow>
          <NavRow end to="/projects" title="Проекты"><FolderKanban size={16} />{!sidebarCollapsed && ' Проекты'}</NavRow>
          <NavRow end to="/workgroups" title="Команды"><Users size={16} />{!sidebarCollapsed && ' Команды'}</NavRow>
          {profile?.admin && (
            <NavRow end to="/admin" title="Администрирование"><ShieldCheck size={16} />{!sidebarCollapsed && ' Админ'}</NavRow>
          )}
        </NavGroup>

        {!sidebarCollapsed && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 12 }}>
              <SectionHeader $open={boardsOpen} onClick={() => setBoardsOpen((o) => !o)} style={{ flex: 1 }}>
                <ChevronDown size={12} />
                Мои доски
                {(boards ?? []).length > 0 && <span style={{ marginLeft: 4, opacity: 0.5 }}>{(boards ?? []).length}</span>}
              </SectionHeader>
              <NavLink to="/boards?new=1" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, color: 'var(--text-tertiary)', transition: 'all 0.1s ease' }} title="Создать доску"
                onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent)'; }}
                onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-tertiary)'; }}
              >
                <Plus size={14} />
              </NavLink>
            </div>
            <NavGroup $collapsed={!boardsOpen}>
              {(boards ?? []).length === 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: '8px 12px' }}>Нет досок</span>
              )}
              {personalBoards.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 8px 2px', display: 'block' }}>Личные</span>
                  {personalBoards.slice(0, 8).map((b, i) => (
                    <NavRow key={b.id} to={`/boards/${b.id}`} title={b.projectName ? `${b.name} · ${b.projectName}` : b.name}>
                      <BoardDot $c={boardColors[i % boardColors.length]} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                      <NavCount>{b.shortCode}</NavCount>
                    </NavRow>
                  ))}
                </div>
              )}
              {teamBoards.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 8px 2px', display: 'block' }}>Командные</span>
                  {teamBoards.slice(0, 8).map((b, i) => (
                    <NavRow key={b.id} to={`/boards/${b.id}`} title={b.workgroupName ? `${b.name} · ${b.workgroupName}` : b.name}>
                      <BoardDot $c={boardColors[(i + personalBoards.length) % boardColors.length]} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                      <NavCount>{b.shortCode}</NavCount>
                    </NavRow>
                  ))}
                </div>
              )}
              {(boards ?? []).length > 16 && (
                <NavRow to="/boards" style={{ fontSize: 11, color: 'var(--text-tertiary)', justifyContent: 'center' }}>
                  Показать все ({(boards ?? []).length})
                </NavRow>
              )}
            </NavGroup>

          </>
        )}

        <Spacer />

        <UserSection>
          {sidebarCollapsed ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <NavLink to="/settings/profile" title="Настройки профиля" style={{ textDecoration: 'none' }}>
                <Avatar name={profile?.name ?? session?.userName ?? initial} url={profile?.avatarUrl} color={profile?.avatarColor} size={32} />
              </NavLink>
            </div>
          ) : (
            <>
              <UserRow to="/settings/profile" title="Настройки профиля">
                <Avatar name={profile?.name ?? session?.userName ?? initial} url={profile?.avatarUrl} color={profile?.avatarColor} size={40} />
                <UserInfo>
                  <UserName>{profile?.name ?? session?.userName ?? 'Пользователь'}</UserName>
                  <UserEmail>{profile?.email ?? session?.userEmail ?? ''}</UserEmail>
                </UserInfo>
              </UserRow>
              <ActionRow>
                <SmallBtn onClick={toggle}>
                  {mode === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
                  {mode === 'dark' ? 'Светлая' : 'Тёмная'}
                </SmallBtn>
                <SmallBtn type="button" onClick={() => performLogout()}>
                  <LogOut size={13} />
                  Выйти
                </SmallBtn>
              </ActionRow>
            </>
          )}
        </UserSection>
      </Sidebar>

      <Content>
        <TopBar>
          <BreadcrumbNav>
            <BreadcrumbLink to="/boards" style={{ opacity: 0.7 }}>{APP_NAME}</BreadcrumbLink>
            {breadcrumbItems.map((item) => (
              <React.Fragment key={item.href}>
                <BcSep>/</BcSep>
                {item.current ? (
                  <BreadcrumbCurrent>{item.label}</BreadcrumbCurrent>
                ) : (
                  <BreadcrumbLink to={item.href}>{item.label}</BreadcrumbLink>
                )}
              </React.Fragment>
            ))}
          </BreadcrumbNav>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <SmallBtn onClick={() => setPaletteOpen(true)} style={{ flex: 'none', padding: '5px 8px' }} title="Поиск (⌘K)">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/></svg>
            </SmallBtn>
            <SmallBtn onClick={() => setNotifOpen((p) => !p)} style={{ flex: 'none', padding: '5px 8px', position: 'relative' }} title="Уведомления">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M4 6a4 4 0 018 0c0 2.5 1 4 1 4H3s1-1.5 1-4z"/>
                <path d="M6.5 13a1.5 1.5 0 003 0"/>
              </svg>
              {unreadCount > 0 ? <Badge>{unreadCount > 9 ? '9+' : unreadCount}</Badge> : null}
            </SmallBtn>
            <SmallBtn onClick={() => setHelpOpen(true)} style={{ flex: 'none', padding: '5px 8px' }} title="Клавиши (?)">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="4" width="12" height="8" rx="2"/><path d="M5 8h1M8 8h1M11 8h1"/></svg>
            </SmallBtn>
          </div>
        </TopBar>
        <MobileNav>
          <MobileNavLink end to="/boards"><LayoutDashboard size={16} /> Доски</MobileNavLink>
          <MobileNavLink end to="/my-work"><Star size={16} /> Моя работа</MobileNavLink>
          <MobileNavLink end to="/projects"><FolderKanban size={16} /> Проекты</MobileNavLink>
          <MobileNavLink end to="/workgroups"><Users size={16} /> Команды</MobileNavLink>
          {profile?.admin && <MobileNavLink end to="/admin"><ShieldCheck size={16} /> Админ</MobileNavLink>}
        </MobileNav>
        <MainArea key={location.pathname}>
          {children}
        </MainArea>
      </Content>

      {paletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            boardId={paletteBoardContext.boardId}
            onCreateCard={paletteBoardContext.onCreateCard}
            onCreateColumn={paletteBoardContext.onCreateColumn}
            onToggleActivity={paletteBoardContext.onToggleActivity}
          />
        </Suspense>
      )}
      {helpOpen && (
        <Suspense fallback={null}>
          <KeyboardHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
        </Suspense>
      )}
      <NotificationsDropdown open={notifOpen} onClose={() => setNotifOpen(false)} />
    </Shell>
  );
};
